import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import ListItemText from '@mui/material/ListItemText';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import AddIcon from '@mui/icons-material/Add';

const formatSearchDate = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString();
};

export default function ControlCenterSavedSearchesBar({
  savedSearches,
  defaultSearchId,
  activeSearchId,
  onApplySearch,
  onSaveSearch,
  onDeleteSearch,
  onRenameSearch,
  onSetDefault,
  onClearDefault,
  onResetFilters,
  isDarkMode,
}) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [saveDialogOpen, setSaveDialogOpen] = React.useState(false);
  const [manageDialogOpen, setManageDialogOpen] = React.useState(false);
  const [saveName, setSaveName] = React.useState('');
  const [setAsDefault, setSetAsDefault] = React.useState(false);
  const [editingSearchId, setEditingSearchId] = React.useState(null);
  const [editingName, setEditingName] = React.useState('');

  const activeSearch = savedSearches.find((search) => search.id === activeSearchId) || null;
  const triggerLabel = activeSearch?.name || 'Saved search';

  const openSaveDialog = () => {
    setSaveName('');
    setSetAsDefault(false);
    setSaveDialogOpen(true);
  };

  const handleSave = () => {
    const trimmedName = saveName.trim();
    if (!trimmedName) return;
    onSaveSearch({ name: trimmedName, setAsDefault });
    setSaveDialogOpen(false);
  };

  const startRename = (search) => {
    setEditingSearchId(search.id);
    setEditingName(search.name);
  };

  const commitRename = () => {
    const trimmedName = editingName.trim();
    if (!trimmedName || !editingSearchId) return;
    onRenameSearch(editingSearchId, trimmedName);
    setEditingSearchId(null);
    setEditingName('');
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
        <Button
          onClick={(e) => setAnchorEl(e.currentTarget)}
          startIcon={<BookmarkBorderIcon sx={{ fontSize: 15 }} />}
          endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
          sx={{
            textTransform: 'none',
            color: '#8e8e93',
            fontSize: 13,
            fontWeight: 500,
            px: 1,
            minWidth: 'auto',
            whiteSpace: 'nowrap',
          }}
        >
          {triggerLabel}
        </Button>
        <Tooltip title="Manage saved searches">
          <IconButton size="small" onClick={() => setManageDialogOpen(true)} sx={{ color: '#8e8e93' }}>
            <SettingsOutlinedIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { borderRadius: 2.5, mt: 0.5, minWidth: 230 } } }}
      >
        <Box sx={{ p: 0.75 }}>
          <ListItemButton
            selected={!activeSearchId}
            onClick={() => {
              onResetFilters();
              setAnchorEl(null);
            }}
            sx={{ borderRadius: 1.5, mb: 0.25 }}
          >
            <ListItemText primary="Default" primaryTypographyProps={{ fontSize: 13.5 }} />
          </ListItemButton>
          {savedSearches.map((search) => (
            <ListItemButton
              key={search.id}
              selected={activeSearchId === search.id}
              onClick={() => {
                onApplySearch(search.id);
                setAnchorEl(null);
              }}
              sx={{ borderRadius: 1.5, mb: 0.25, gap: 0.75 }}
            >
              {defaultSearchId === search.id ? (
                <StarIcon sx={{ fontSize: 15, color: 'warning.main' }} />
              ) : (
                <StarBorderIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
              )}
              <ListItemText primary={search.name} primaryTypographyProps={{ fontSize: 13.5 }} />
            </ListItemButton>
          ))}
          <ListItemButton
            onClick={() => {
              setAnchorEl(null);
              openSaveDialog();
            }}
            sx={{ borderRadius: 1.5, color: 'primary.main' }}
          >
            <AddIcon sx={{ fontSize: 16, mr: 0.75 }} />
            <ListItemText primary="New saved search" primaryTypographyProps={{ fontSize: 13.5, fontWeight: 500 }} />
          </ListItemButton>
        </Box>
      </Popover>

      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 600 }}>Save search</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Name"
            value={saveName}
            onChange={(event) => setSaveName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleSave();
              }
            }}
          />
          <FormControlLabel
            control={(
              <Checkbox
                checked={setAsDefault}
                onChange={(event) => setSetAsDefault(event.target.checked)}
              />
            )}
            label="Set as default (auto-apply on load)"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!saveName.trim()}
            sx={{
              backgroundColor: isDarkMode ? '#fff' : '#000',
              color: isDarkMode ? '#000' : '#fff',
              '&:hover': { backgroundColor: isDarkMode ? '#e0e0e0' : '#333' },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={manageDialogOpen} onClose={() => setManageDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 600 }}>Saved searches</DialogTitle>
        <DialogContent>
          {savedSearches.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              No saved searches yet.
            </Typography>
          ) : (
            <List dense>
              {savedSearches.map((search) => (
                <Box
                  component="div"
                  key={search.id}
                  sx={{ display: 'flex', alignItems: 'center', px: 0 }}
                >
                  {editingSearchId === search.id ? (
                    <TextField
                      fullWidth
                      size="small"
                      value={editingName}
                      onChange={(event) => setEditingName(event.target.value)}
                      onBlur={commitRename}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          commitRename();
                        }
                      }}
                    />
                  ) : (
                    <ListItemText
                      primary={(
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          {defaultSearchId === search.id ? (
                            <StarIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                          ) : (
                            <StarBorderIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                          )}
                          <Typography component="span" sx={{ fontSize: '0.9rem' }}>
                            {search.name}
                          </Typography>
                        </Box>
                      )}
                      secondary={formatSearchDate(search.updatedAt || search.createdAt)}
                    />
                  )}
                  <ListItemSecondaryAction sx={{ position: 'static', display: 'flex', ml: 'auto' }}>
                    <Tooltip title="Rename">
                      <IconButton edge="end" size="small" onClick={() => startRename(search)}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={defaultSearchId === search.id ? 'Clear default' : 'Set as default'}>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => {
                          if (defaultSearchId === search.id) {
                            onClearDefault();
                          } else {
                            onSetDefault(search.id);
                          }
                        }}
                      >
                        {defaultSearchId === search.id ? (
                          <StarIcon fontSize="small" sx={{ color: 'warning.main' }} />
                        ) : (
                          <StarBorderIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton edge="end" size="small" onClick={() => onDeleteSearch(search.id)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </Box>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setManageDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
